/**
 * LoadingOverlay Component
 *
 * Global loading overlay displayed during async operations.
 * Uses portal to render above all content with high z-index.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useRecommendations, useProduct } from '../contexts';
import { t } from '../i18n';

export interface LoadingOverlayProps {
  message?: string;
  show?: boolean;
}

/**
 * Spinner component
 */
const Spinner: React.FC = () => (
  <div className="relative">
    <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
  </div>
);

/**
 * LoadingOverlay component
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ message, show: propShow }) => {
  // Initialize mounted state for SSR compatibility
  // Returns false on server, true on client after mount
  const [mounted] = useState(() => {
    if (typeof window === 'undefined') return false;
    return true;
  });

  // Get loading states from all contexts
  const auth = useAuth();
  const recommendations = useRecommendations();
  const product = useProduct();

  // Determine if loading overlay should be shown
  const isLoading =
    propShow !== undefined
      ? propShow
      : auth.loading || recommendations.loading || product.loading;

  // Don't render until mounted (for SSR compatibility)
  if (!mounted || !isLoading) {
    return null;
  }

  const overlayContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      role="alert"
      aria-live="assertive"
      aria-busy="true"
    >
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm mx-4">
        <div className="flex flex-col items-center">
          <Spinner />
          {message && (
            <p className="mt-4 text-gray-700 text-center font-medium">{message}</p>
          )}
          {!message && (
            <p className="mt-4 text-gray-700 text-center">{t('loading.default')}</p>
          )}
        </div>
      </div>
    </div>
  );

  // Render overlay as portal to body
  return createPortal(overlayContent, document.body);
};

/**
 * Simplified loading overlay without context integration
 * Useful for manual loading state control
 */
export const SimpleLoadingOverlay: React.FC<LoadingOverlayProps> = ({ message, show = false }) => {
  // Initialize mounted state for SSR compatibility
  // Returns false on server, true on client after mount
  const [mounted] = useState(() => {
    if (typeof window === 'undefined') return false;
    return true;
  });

  if (!mounted || !show) {
    return null;
  }

  const overlayContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      role="alert"
      aria-live="assertive"
      aria-busy="true"
    >
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-sm mx-4">
        <div className="flex flex-col items-center">
          <Spinner />
          {message && (
            <p className="mt-4 text-gray-700 text-center font-medium">{message}</p>
          )}
          {!message && (
            <p className="mt-4 text-gray-700 text-center">{t('loading.default')}</p>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
};
